import CloseIcon from 'app/ui_v2/icons/CloseIcon';
import { useEffect, useState } from 'react';

import { Box, IconButton, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import ButtonLoader from '../button-loader/ButtonLoader';

export type ConfirmationDialogSubmitButtonClass = 'success' | 'fail' | 'primary' | 'secondary';

export interface ConfirmationDialogProps {
  title: string | JSX.Element;
  description: string | JSX.Element;
  opened: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  confirmButtonVariant?: 'outlined' | 'contained';
  confirmButtonClass?: ConfirmationDialogSubmitButtonClass;
  confirmButtonText?: string;
  isWithClosingIcon?: boolean;
  confirmButtonDisabled?: boolean;
  titleHeaderVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  isLoading?: boolean;
}

export const ConfirmationDialog = (props: ConfirmationDialogProps) => {
  const {
    title,
    description,
    opened,
    onConfirm,
    onCancel,
    confirmButtonVariant = 'outlined',
    confirmButtonClass = '',
    confirmButtonText = 'Confirm',
    isWithClosingIcon = false,
    confirmButtonDisabled = false,
    titleHeaderVariant = 'h4',
    isLoading = false,
  } = props;
  const [open, setOpen] = useState(opened);

  useEffect(() => {
    setOpen(opened);
  }, [opened]);

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onCancel && onCancel();
  };

  return (
    <Dialog
      maxWidth="md"
      open={open}
      onClose={handleCancel}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {typeof title === 'string' && (
          <Box color="primary.dark">
            <Typography component="h4" variant={titleHeaderVariant}>
              {title}
            </Typography>
          </Box>
        )}
        {typeof title !== 'string' && title}
        {isWithClosingIcon && (
          <IconButton
            aria-label="close"
            onClick={() => handleCancel()}
            style={{ position: 'absolute', top: 8, right: 15, color: ' ' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        {typeof description === 'string' && (
          <DialogContentText id="alert-dialog-description">{description}</DialogContentText>
        )}
        {typeof description !== 'string' && description}
      </DialogContent>
      <DialogActions disableSpacing>
        {onCancel && (
          <Box mr={2}>
            <Button variant="outlined" onClick={handleCancel} autoFocus>
              Cancel
            </Button>
          </Box>
        )}
        <Button
          color={confirmButtonClass ? 'primary' : 'secondary'}
          variant={confirmButtonVariant}
          onClick={handleConfirm}
          className={confirmButtonClass}
          disableElevation
          disabled={confirmButtonDisabled}
        >
          <ButtonLoader isActive={isLoading} />
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
